import React, { useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const SEVERITY_STYLES = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: 'Crítico' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'Atenção' },
  info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', label: 'Info' },
};

export default function ComplianceView() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const params = filter ? { severity: filter } : {};
      const res = await api.get('/compliance/alerts', { params });
      setAlerts(res.data?.data || []);
    } catch {
      toast.error('Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const resolve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/compliance/alerts/${id}/resolve`);
      toast.success('Alerta resolvido');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao resolver alerta');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Carregando Compliance...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Compliance</h1>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium">{alerts.filter((a) => !a.is_resolved).length} ativos</span>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtrar por Severidade</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
          >
            <option value="">Todas</option>
            <option value="critical">Crítico</option>
            <option value="warning">Atenção</option>
            <option value="info">Info</option>
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alertas Fiscais</CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum alerta fiscal.</p>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => {
                const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
                return (
                  <div
                    key={alert.id}
                    className={`rounded-lg border p-4 ${style.bg} ${style.border}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium">{alert.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {alert.driver_name} • {new Date(alert.created_at).toLocaleDateString('pt-PT')} •
                          <span className={`ml-1 font-medium ${style.text}`}>{style.label}</span>
                        </p>
                      </div>
                      {!alert.is_resolved && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolve(alert.id)}
                          disabled={busy === alert.id}
                        >
                          {busy === alert.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Resolver'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
